// Stale-while-revalidate cache for the minimal /subjects payload.

const SCHEMA = 1
export const SUBJECTS_AUX_CACHE_KEY = 'subjects.aux-cache.v1'

export interface SubjectsCacheSnapshot<S, P, G> {
  schema: number
  userId: string
  mode: string
  selectedUniversity: string | null
  cachedAt: number
  subjects: S[]
  studentProgrammes: P[]
  hasChosen: boolean
  subjectGate: G | null
}

function keyFor(userId: string, mode: string, selectedUniversity: string | null): string {
  return `subjects-cache-v${SCHEMA}-${userId}-${mode}-${selectedUniversity ?? 'all'}`
}

export function readSubjectsCache<S, P, G>(
  userId: string | undefined,
  mode: string,
  selectedUniversity: string | null,
): SubjectsCacheSnapshot<S, P, G> | null {
  if (!userId) return null
  try {
    const raw = localStorage.getItem(keyFor(userId, mode, selectedUniversity))
    if (!raw) return null
    const parsed = JSON.parse(raw) as SubjectsCacheSnapshot<S, P, G>
    if (parsed.schema !== SCHEMA) return null
    if (parsed.userId !== userId) return null
    if (parsed.mode !== mode) return null
    if ((parsed.selectedUniversity ?? null) !== (selectedUniversity ?? null)) return null
    return parsed
  } catch {
    return null
  }
}

export function writeSubjectsCache<S, P, G>(
  snapshot: Omit<SubjectsCacheSnapshot<S, P, G>, 'schema' | 'cachedAt'>,
): void {
  if (!snapshot.userId) return
  try {
    const full: SubjectsCacheSnapshot<S, P, G> = {
      schema: SCHEMA,
      cachedAt: Date.now(),
      ...snapshot,
    }
    localStorage.setItem(
      keyFor(snapshot.userId, snapshot.mode, snapshot.selectedUniversity),
      JSON.stringify(full),
    )
  } catch {
  }
}

// Compares only the data slice that matters for rendering the subject grid.
export function subjectsPayloadEquals<S, P, G>(
  a: Pick<SubjectsCacheSnapshot<S, P, G>, 'subjects' | 'studentProgrammes' | 'hasChosen' | 'subjectGate'>,
  b: Pick<SubjectsCacheSnapshot<S, P, G>, 'subjects' | 'studentProgrammes' | 'hasChosen' | 'subjectGate'>,
): boolean {
  if (a.hasChosen !== b.hasChosen) return false
  if (JSON.stringify(a.subjects) !== JSON.stringify(b.subjects)) return false
  if (JSON.stringify(a.studentProgrammes) !== JSON.stringify(b.studentProgrammes)) return false
  if (JSON.stringify(a.subjectGate) !== JSON.stringify(b.subjectGate)) return false
  return true
}

// Drops every cached /subjects variant for the given user.
export function invalidateSubjectsCache(userId: string | undefined): void {
  if (!userId) return
  try {
    const prefix = `subjects-cache-v${SCHEMA}-${userId}-`
    const toRemove: string[] = []
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix)) toRemove.push(key)
    }
    for (const key of toRemove) localStorage.removeItem(key)
  } catch {
  }
}

export function invalidateSubjectsAuxCache(): void {
  try {
    sessionStorage.removeItem(SUBJECTS_AUX_CACHE_KEY)
  } catch {
  }
}
